// Imports: choose the kind, pick the file, review every row, apply.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api, ApiError } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { SelectField } from '../ui/Fields.tsx';
import { Table } from '../ui/Table.tsx';
import { Status } from '../ui/Status.tsx';
import { moment } from '../format.ts';

type Kind = { kind: string; fields: Array<{ key: string; required: boolean; aliases: string[] }> };
type Stats = {
  rows: number;
  ny: number;
  andrad: number;
  oforandrad: number;
  fel: number;
  columns: Record<string, string | null>;
};
type Row = {
  rowNumber: number;
  raw: Record<string, string>;
  action: 'ny' | 'andrad' | 'oforandrad' | 'fel';
  mapped: Record<string, string | number | null>;
  errors: string[];
};
type Preview = { id: string; stats: Stats; sample: Row[] };
type Batch = {
  id: string;
  kind: string;
  fileName: string;
  status: string;
  stats: Partial<Stats> & { created?: number; updated?: number; skipped?: number };
  createdAt: string;
  appliedAt: string | null;
};

const tone = { ny: 'done', andrad: 'active', oforandrad: 'none', fel: 'late' } as const;

export function ImportsPage() {
  const h1 = usePage(sv.imports.title);
  const qc = useQueryClient();
  const kinds = useQuery({
    queryKey: ['import-kinds'],
    queryFn: () => api<Kind[]>('/imports/kinds'),
  });
  const history = useQuery({ queryKey: ['imports'], queryFn: () => api<Batch[]>('/imports') });
  const [kind, setKind] = useState('ayvens');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: async () => {
      const text = await file!.text();
      return api<Preview>(`/imports/${kind}?name=${encodeURIComponent(file!.name)}`, {
        method: 'POST',
        body: text,
        headers: { 'Content-Type': 'text/csv' },
      });
    },
    onSuccess: async (p) => {
      setPreview(p);
      setError(null);
      setDone(null);
      await qc.invalidateQueries({ queryKey: ['imports'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError && err.message ? err.message : sv.errors.server),
  });
  const apply = useMutation({
    mutationFn: () =>
      api<{ created: number; updated: number; skipped: number }>(`/imports/${preview!.id}/apply`, {
        method: 'POST',
      }),
    onSuccess: async (r) => {
      setDone(sv.imports.applied(r));
      setPreview(null);
      setFile(null);
      await qc.invalidateQueries({ queryKey: ['imports'] });
    },
    onError: () => setError(sv.errors.server),
  });
  const [zip, setZip] = useState<File | null>(null);
  const [zipMessage, setZipMessage] = useState<string | null>(null);
  const uploadZip = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      body.append('file', zip!);
      return api<{ attached: number; skipped: number; unknown: string[] }>(
        '/imports/fleet360-files',
        { method: 'POST', body },
      );
    },
    onSuccess: (r) => {
      setZip(null);
      setZipMessage(sv.documents.fleetZipDone(r));
    },
    onError: () => setZipMessage(sv.errors.server),
  });
  const def = kinds.data?.find((k) => k.kind === kind);
  const fieldLabel = (k: string) => sv.imports.fields[k] ?? k;
  return (
    <>
      <PageHead
        help="imports"
        crumbs={[{ to: '/admin', label: sv.adminHome.title }, { label: sv.imports.title }]}
        title={sv.imports.title}
        titleRef={h1}
      />
      <p>{sv.imports.intro}</p>
      {done ? (
        <p className="notice" data-state="done" role="status">
          {done}
        </p>
      ) : null}
      <form
        className="form-block"
        onSubmit={(e) => {
          e.preventDefault();
          if (file) upload.mutate();
        }}
      >
        <SelectField
          id="kind"
          label={sv.imports.kind}
          value={kind}
          options={Object.entries(sv.imports.kinds).map(([value, label]) => ({ value, label }))}
          onChange={(v) => {
            setKind(v);
            setPreview(null);
          }}
        />
        {def ? (
          <p className="caption">
            {sv.imports.columnsHelp}:{' '}
            {def.fields
              .map((f) => `${fieldLabel(f.key)}${f.required ? '*' : ''} (${f.aliases.join(', ')})`)
              .join('; ')}
          </p>
        ) : null}
        <div className="field">
          <label className="label" htmlFor="file">
            {sv.imports.file}
          </label>
          <span className="help" id="file-help">
            {sv.imports.fileHelp}
          </span>
          <input
            className="input"
            id="file"
            type="file"
            accept=".csv,.txt,text/csv"
            aria-describedby="file-help"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        {error ? (
          <p className="notice" data-state="bad" role="alert">
            {error}
          </p>
        ) : null}
        <div className="form-actions">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={!file || upload.isPending}
            aria-busy={upload.isPending}
          >
            {upload.isPending ? sv.imports.uploading : sv.imports.upload}
          </button>
        </div>
      </form>
      {preview ? (
        <section className="block" aria-labelledby="preview">
          <h2 className="block-title" id="preview">
            {sv.imports.preview}
          </h2>
          <p role="status">{sv.imports.stats(preview.stats)}</p>
          <p className="caption">
            {sv.imports.columnsFound}:{' '}
            {Object.entries(preview.stats.columns)
              .filter(([, c]) => c)
              .map(([k, c]) => `${fieldLabel(k)} = ${c}`)
              .join(', ')}
          </p>
          <Table caption={sv.imports.sample} hasOptional>
            <thead>
              <tr>
                <th scope="col" className="r">
                  {sv.imports.row}
                </th>
                <th scope="col">{sv.columns.status}</th>
                <th scope="col">{sv.columns.registration}</th>
                <th scope="col" className="col-opt">
                  {sv.columns.description}
                </th>
              </tr>
            </thead>
            <tbody>
              {preview.sample.map((r) => (
                <tr key={r.rowNumber}>
                  <td className="r">{r.rowNumber}</td>
                  <td>
                    <Status tone={tone[r.action]}>
                      {sv.imports.actions[r.action] ?? r.action}
                    </Status>
                    {r.errors.length ? (
                      <span className="caption">
                        {' '}
                        {r.errors
                          .map((e) => sv.imports.errors[e] ?? sv.imports.errorField(fieldLabel(e)))
                          .join(', ')}
                      </span>
                    ) : null}
                  </td>
                  <td className="num">{String(r.mapped.plate ?? '')}</td>
                  <td className="col-opt">
                    {Object.entries(r.mapped)
                      .filter(([k, v]) => k !== 'plate' && v !== null && v !== '')
                      .map(([k, v]) => `${fieldLabel(k)}: ${v}`)
                      .join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="form-actions">
            <button
              className="btn btn-primary"
              type="button"
              disabled={apply.isPending || preview.stats.ny + preview.stats.andrad === 0}
              aria-busy={apply.isPending}
              onClick={() => apply.mutate()}
            >
              {sv.imports.apply}
            </button>
          </div>
        </section>
      ) : null}
      <form
        className="form-block"
        aria-labelledby="fleet-zip"
        onSubmit={(e) => {
          e.preventDefault();
          if (zip) uploadZip.mutate();
        }}
      >
        <h2 className="form-heading" id="fleet-zip">
          {sv.documents.fleetZip}
        </h2>
        <div className="field">
          <label className="label" htmlFor="zip">
            {sv.documents.fleetZip}
          </label>
          <span className="help" id="zip-help">
            {sv.documents.fleetZipHelp}
          </span>
          <input
            className="input"
            id="zip"
            type="file"
            accept=".zip,application/zip"
            aria-describedby="zip-help"
            onChange={(e) => setZip(e.target.files?.[0] ?? null)}
          />
        </div>
        {zipMessage ? (
          <p className="notice" data-state="done" role="status">
            {zipMessage}
          </p>
        ) : null}
        <div className="form-actions">
          <button
            className="btn"
            type="submit"
            disabled={!zip || uploadZip.isPending}
            aria-busy={uploadZip.isPending}
          >
            {sv.documents.fleetZipButton}
          </button>
        </div>
      </form>
      <Table caption={sv.imports.history} isEmpty={(history.data?.length ?? 0) === 0} hasOptional>
        <thead>
          <tr>
            <th scope="col">{sv.columns.date}</th>
            <th scope="col">{sv.imports.kind}</th>
            <th scope="col" className="col-opt">
              {sv.columns.description}
            </th>
            <th scope="col">{sv.columns.status}</th>
            <th scope="col" className="col-opt">
              {sv.columns.count}
            </th>
          </tr>
        </thead>
        <tbody>
          {history.data?.map((b) => (
            <tr key={b.id}>
              <td className="num">{moment(b.createdAt)}</td>
              <td>{sv.imports.kinds[b.kind] ?? b.kind}</td>
              <td className="col-opt">{b.fileName}</td>
              <td>
                <Status
                  tone={
                    b.status === 'genomford' ? 'done' : b.status === 'granskad' ? 'waiting' : 'none'
                  }
                >
                  {sv.imports.status[b.status] ?? b.status}
                </Status>
              </td>
              <td className="col-opt">
                {b.status === 'genomford'
                  ? `${b.stats.created ?? 0} + ${b.stats.updated ?? 0}`
                  : `${b.stats.rows ?? 0}`}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}
